/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type {
  BalanceRegistration,
  OfflinePaymentBatch,
} from '@sportninja/common/services/teamBalance'
import colors from '@sportninja/common/constants/appColors'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import OfflineBatchRow from './OfflineBatchRow'

type OfflineHistorySectionProps = {
  registrations: BalanceRegistration[]
  /** Receives the batch, whose own `line_kind` the void sends (Sep 23). */
  onVoidOffline: (batch: OfflinePaymentBatch) => void
  voidingBatchId?: string | null
}

type HistoryEntry = {
  batch: OfflinePaymentBatch
  currencyCode: string
  divisionName: string
}

/**
 * R3-F4 — the registration-level DETACHED offline payment history: batches whose slot/player
 * pairing no longer holds (released players, reassigned or deleted slots). Rendered apart from
 * the slots table so a prior occupant's cash is never read as the current player's; each entry
 * names who the money was recorded for and stays voidable (server-guarded).
 */
const OfflineHistorySection = ({
  registrations,
  onVoidOffline,
  voidingBatchId,
}: OfflineHistorySectionProps) => {
  const entries: HistoryEntry[] = registrations.flatMap((registration) =>
    (registration.offline_payment_history ?? []).map((batch) => ({
      batch,
      currencyCode: registration.currency?.code ?? 'CAD',
      divisionName: registration.division?.name ?? '',
    }))
  )

  if (entries.length === 0) {
    return null
  }

  const showDivision = registrations.length > 1

  return (
    <div
      data-testid='offline-payment-history'
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
    >
      <span
        css={css`
          font-size: 12px;
          color: ${colors.NEUTRAL_100};
        `}
      >
        {balanceLabel('common:balanceOfflineHistoryTitle')}
      </span>
      <span
        css={css`
          font-size: 12px;
          color: ${colors.NEUTRAL_100};
        `}
      >
        {balanceText('common:balanceOfflineHistoryHint')}
      </span>
      {entries.map(({ batch, currencyCode, divisionName }) => (
        <OfflineBatchRow
          key={batch.id}
          batch={batch}
          currencyCode={currencyCode}
          onVoidOffline={onVoidOffline}
          voidingBatchId={voidingBatchId}
          playerName={
            batch.player?.name_full
              ? showDivision && divisionName
                ? `${batch.player.name_full} · ${divisionName}`
                : batch.player.name_full
              : null
          }
        />
      ))}
    </div>
  )
}

export default OfflineHistorySection
